import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '@components/loader/Loader';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { headers, steps } from './content';
import {
  activatePerson,
  deactivatePerson,
  getPersonDetailsAddressList,
  getPersonDetailsInfo,
} from './queries';
import {
  entityToPersonDetails,
  getPersonStatus,
  mapToPersonAddressesListingData,
} from './utils';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import EnhancedStepperSpecificDesign from '@components/stepper/EnhancedStepperSpecificDesign';
import { makeStyles } from 'tss-react/mui';
import StaticLayout from '@components/layout/StaticLayout';
import EnhancedButton, {
  EnhancedButtonStatus,
} from '@components/_old/EnhancedButton';
import { IConfirmation } from '@redux/confirmation/types';
import { isEmpty } from 'lodash';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { IListingData } from '@models/listing';
import {
  capitalizeFirstCharacter,
  capitalizeFirstLetter,
} from '@utils/formatting-utils';
import ShowForUser from '@components/common/ShowForUser';
import PersonalInformationDetailsWidget from './widgets/PersonalInformationDetailsWidget';
import PersonEntityInfoWidget from './widgets/PersonEntityInfoWidget';
import AdditionalInformationDetailsWidget from './widgets/AdditionalInformationDetailsWidget';
import { IAbstractRecord } from '@models';
import { ActionTarget, IEnhancedMenuItem } from '@components/table/types';
import PersonDetailsDrawer from './personal-information-drawer/PersonDetailsDrawer';
import { IPersonDetailsDrawerInfo } from './personal-information-drawer';
import AdditionalInformationDrawer from './person-additional-information-drawer/AdditionalInformationDrawer';
import ListingTableWithPagination from '@components/table/with-pagination/ListingTableWithPagination';
import PersonAddressDrawer from './person-address-drawer/PersonAddressDrawer';
import { CDN_URL } from '@utils';

const useStyles = makeStyles()(() => ({
  stepper: {
    backgroundColor: 'transparent',
    maxWidth: '90%',
    marginBottom: '20px',
    '& .MuiStepLabel-root .Mui-active, .MuiStepLabel-root ': {
      color: MAIN_THEME.color.black,
      '& .MuiStepIcon-text': {
        fontSize: 0,
        lineHeight: '0px',
        fill: 'black',
      },
    },

    '.Mui-completed': {
      color: MAIN_THEME.color.black,
      '& .MuiStepIcon-text': {
        fontSize: 0,
        lineHeight: '0px',
        fill: 'black',
      },
    },

    '@media only screen and (min-width: 600px)': {
      maxWidth: '1300px',
    },
  },
  section: {
    display: 'grid',
    'grid-template-columns': '19.5% 19.5% 19.5% 19.5% 19.5%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',

    '& label': {
      '& span': {
        margin: '-2px 0',
      },
    },
  },
  sectionTitle: {
    padding: '-10x 10x!important',
  },
  sectionFullRow: {
    display: 'grid',
    'grid-template-columns': '100%',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  row: {
    flexWrap: 'wrap',
    margin: '15px 20px 10px 0px',
    alignItems: 'flex-start',
    display: 'grid',
    gap: '5px',
    '& label': {
      flexGrow: '1',
      flexBasis: '0',
      minWidth: '0',
    },
    'grid-template-columns': '19.5% 19.5% 19.5% 19% 16% 3% 3%',
  },
  thickSeperator: {
    height: 10,
    margin: '10px 0 10px 0',
    'grid-column-start': '1',
    'grid-column-end': '4',
  },
  field: {
    width: '96%',
    marginRight: '10px !important',
  },
  repeaterField: {
    margin: '0px!important',
  },
  arField: {
    width: '96%',
    marginRight: '10px !important',
    direction: 'rtl',
  },
  header: {
    fontFamily: MAIN_THEME.font.bold,
    fontSize: MAIN_THEME.fontSize.rg,
    color: MAIN_THEME.color.black,
    '&:hover': {
      color: MAIN_THEME.color.black,
    },
  },
  content: {
    padding: '0',
  },
  dirtyChip: {
    margin: '0 0 -15px 0',
    backgroundColor: MAIN_THEME.color.black,
    '& span': {
      color: MAIN_THEME.color.white,
    },
  },
  tabPanelStyle: {
    padding: 'unset',
    marginTop: '20px',
  },
  actionButton: {
    display: 'inline-block',
    marginRight: '5px',
  },
}));

const PersonDetailsPage: React.FC = () => {
  const { classes } = useStyles();
  const params = useParams();
  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.user);
  const entityId = params.id;
  const isAdmin = user.userRoles.includes('Insurance-Admin');

  const [activateButtonState, setActivateButtonState] =
    useState<EnhancedButtonStatus>();
  const [deactivateButtonState, setDeactivateButtonState] =
    useState<EnhancedButtonStatus>();
  const [personInfoDrawerOpen, setPersonInfoDrawerOpen] =
    useState<boolean>(false);
  const [personAddressDrawerOpen, setPersonAddressDrawerOpen] = useState<{
    open: boolean;
    id?: string;
  }>({
    open: false,
  });
  const [additionalInfoDrawerOpen, setAdditionalInfoDrawerOpen] =
    useState<boolean>(false);
  const [personDetailsInfo, setPersonDetailsInfo] = useState<IAbstractRecord>(
    {}
  );
  const [tableData, setTableData] = useState<IListingData>({
    pagedItems: {},
    pageSize: 5,
    pageNumber: 1,
    totalCount: 0,
  });

  const [activateAction] = useMutation(activatePerson(), {
    variables: {
      entityId: entityId,
    },
    errorPolicy: 'all',
  });
  const [deactivateAction] = useMutation(deactivatePerson(), {
    variables: {
      entityId: entityId,
    },
  });

  const [entityResultQuery, { loading }] = useLazyQuery(getPersonDetailsInfo());
  const [personAddressListResultQuery] = useLazyQuery(
    getPersonDetailsAddressList()
  );

  const loadEntityData = async () => {
    const result = await entityResultQuery({
      variables: { id: entityId },
      errorPolicy: 'all',
    });

    return result;
  };

  const loadAddressList = async (
    currentPage: number,
    pageSize = tableData.pageSize,
    searchKey?: string,
    order?: string,
    orderBy?: string
  ) => {
    const result = await personAddressListResultQuery({
      variables: {
        currentPage,
        currentPageSize: pageSize,
        selectedPersonID: params.id,
        Attribute: capitalizeFirstCharacter(
          orderBy || 'SalesforceManagement_PersonAddress_IsPrimary'
        ),
        Descending: order == 'asc' ? false : true,
        keywordSearch: searchKey || null,
      },
      errorPolicy: 'all',
    });

    return result;
  };

  const initialize = async () => {
    try {
      const entityData = await loadEntityData();
      const personDetails = await entityToPersonDetails(entityData.data);
      setPersonDetailsInfo(personDetails);
    } catch (error) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const personDrawerListingActions: IEnhancedMenuItem[] = isAdmin
    ? [
        {
          name: 'new',
          title: 'New',
          onClick: () => {
            setPersonAddressDrawerOpen({
              open: true,
            });
          },
          iconUri: CDN_URL + '/icons/add-primary-red.svg',
          target: ActionTarget.Entity,
          rowIndependant: true,
        },
        {
          name: 'edit',
          title: 'Edit',
          onClick: (selected) => {
            setPersonAddressDrawerOpen({
              open: true,
              id: selected?.[0],
            });
          },
          iconUri: CDN_URL + '/icons/edit-secondary.svg',
          target: ActionTarget.Entity,
        },
      ]
    : [];

  async function handlePageChange(
    page: number,
    searchKey?: string,
    rowsPerPage?: number,
    order?: string,
    orderBy?: string
  ) {
    const result = await loadAddressList(
      page,
      rowsPerPage || tableData.pageSize,
      searchKey,
      order,
      orderBy
    );
    const mappedAddressList = mapToPersonAddressesListingData(result.data);
    setTableData(mappedAddressList);
  }

  const renderMainChildren = () => {
    const status = personDetailsInfo?.status?.toLowerCase();
    const isPersonActive = status === 'active';

    return (
      <>
        <div style={{ marginTop: '20px' }}>
          <EnhancedStepperSpecificDesign
            activeStep={getPersonStatus(status)}
            steps={steps}
            className={classes.stepper}
            showStepperButton
            buttonTitle={isPersonActive ? 'Deactivate' : 'Activate'}
            buttonState={
              isPersonActive ? activateButtonState : deactivateButtonState
            }
            buttonOnClick={async () => {
              if (isPersonActive) {
                const confirmation: IConfirmation = {
                  open: true,
                  title: 'Deactivate',
                  message: `Are you sure you want to deactivate this person?`,
                  callback: async () => {
                    try {
                      setDeactivateButtonState('loading');
                      const result = await deactivateAction();
                      if (isEmpty(result.errors)) {
                        initialize();
                        setDeactivateButtonState('success');
                        toast.success(
                          <ToastSuccessMessage>
                            {
                              'Person successfully deactivated. Relevant Business Partners are deactivated as well.'
                            }
                          </ToastSuccessMessage>
                        );
                      } else {
                        setDeactivateButtonState(undefined);
                        toast.error(
                          <ToastErrorMessage>
                            {getError(result)}
                          </ToastErrorMessage>
                        );
                      }
                    } finally {
                      setTimeout(() => {
                        setDeactivateButtonState(undefined);
                      }, 1000);
                    }
                  },
                  submitButtonText: 'Yes',
                  cancelButtonText: 'No',
                };
                dispatch(OpenConfirmationAction(confirmation));
              } else {
                const confirmation: IConfirmation = {
                  open: true,
                  title: 'Activate',
                  message: `Are you sure you want to activate this person?`,
                  callback: async () => {
                    try {
                      setActivateButtonState('loading');
                      const result = await activateAction();
                      if (isEmpty(result.errors)) {
                        initialize();
                        setActivateButtonState('success');
                        toast.success(
                          <ToastSuccessMessage>
                            {'Person successfully activated.'}
                          </ToastSuccessMessage>
                        );
                      } else {
                        setActivateButtonState(undefined);
                        toast.error(
                          <ToastErrorMessage>
                            {getError(result)}
                          </ToastErrorMessage>
                        );
                      }
                    } finally {
                      setTimeout(() => {
                        setActivateButtonState(undefined);
                      }, 1000);
                    }
                  },
                  submitButtonText: 'Yes',
                  cancelButtonText: 'No',
                };
                dispatch(OpenConfirmationAction(confirmation));
              }
            }}
          />
          <PersonalInformationDetailsWidget
            data={personDetailsInfo}
            actions={
              <div>
                <EnhancedButton
                  type="button"
                  backgroundColor={MAIN_THEME.color.black}
                  color={MAIN_THEME.color.white}
                  onClick={() => setPersonInfoDrawerOpen(true)}
                  className={classes.actionButton}
                >
                  Modify
                </EnhancedButton>
              </div>
            }
          />
          {personInfoDrawerOpen && (
            <PersonDetailsDrawer
              open={personInfoDrawerOpen}
              onClose={() => setPersonInfoDrawerOpen(false)}
              onSuccess={() => {
                initialize();
              }}
              personDetailsInfo={
                personDetailsInfo as unknown as IPersonDetailsDrawerInfo
              }
              personId={params.id}
            />
          )}
          {personDetailsInfo?.type?.toUpperCase() === 'PERSON' && (
            <div style={{ marginTop: '20px' }}>
              <AdditionalInformationDetailsWidget
                data={personDetailsInfo}
                actions={
                  <ShowForUser allowedRoles={['Insurance-Admin']}>
                    {/* {status != "inactive" && ( */}
                    <div>
                      <EnhancedButton
                        type="button"
                        backgroundColor={MAIN_THEME.color.black}
                        color={MAIN_THEME.color.white}
                        onClick={() => setAdditionalInfoDrawerOpen(true)}
                        className={classes.actionButton}
                      >
                        Modify
                      </EnhancedButton>
                    </div>
                    {/* )} */}
                  </ShowForUser>
                }
              />
              {additionalInfoDrawerOpen && (
                <AdditionalInformationDrawer
                  open={additionalInfoDrawerOpen}
                  onClose={() => setAdditionalInfoDrawerOpen(false)}
                  onSuccess={() => {
                    initialize();
                  }}
                  personId={entityId}
                  personType={personDetailsInfo?.type}
                />
              )}
            </div>
          )}
          <>
            <WidgetSection style={{ margin: '-23px 0 0 0' }}>
              <form>
                <div
                  style={{
                    marginTop: '20px',
                    padding: '-23px 0 0 0 !important',
                  }}
                >
                  <ListingTableWithPagination
                    inlineTitle={capitalizeFirstLetter('Addresses Information')}
                    name="address"
                    orderByAscendingByDefault
                    defaultOrderByColumn="SalesforceManagement_PersonAddress_IsPrimary"
                    inline
                    data={tableData}
                    headers={headers}
                    onPageChange={handlePageChange}
                    actions={personDrawerListingActions}
                  />

                  {personAddressDrawerOpen.open && (
                    <PersonAddressDrawer
                      open={personAddressDrawerOpen.open}
                      personAddressId={personAddressDrawerOpen.id}
                      onClose={() =>
                        setPersonAddressDrawerOpen({
                          open: false,
                        })
                      }
                      onSuccess={() => {
                        handlePageChange(1);
                      }}
                      personId={entityId}
                    />
                  )}
                </div>
              </form>
            </WidgetSection>
          </>
        </div>
      </>
    );
  };

  const renderLeftSection = () => {
    return <PersonEntityInfoWidget data={personDetailsInfo} />;
  };

  return loading ? (
    <Loader />
  ) : (
    <StaticLayout
      name={'Person Details'}
      leftChildren={renderLeftSection()}
      mainChildren={renderMainChildren()}
    />
  );
};

export default PersonDetailsPage;
